// src/pages/LocalApplicationsFormPage.js
// Rescoped for Local Applications specifically — multiple Access Owners and
// Billing Owners instead of a single owner, a Security section, a Server
// field instead of URL, and Active Directory / Individual auth types. See
// chat for the field list this was rescoped against.
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PeoplePicker from "../components/PeoplePicker";
import { SystemImageUpload } from "../components/SystemImage";
import { localApplicationsApi as api } from "../utils/api";
import { loadConfig } from "../utils/config";

const SYSTEM_TYPE_OPTIONS = ["Server App", "Local App", "Other"];
const TWO_FACTOR_OPTIONS = ["Enforced", "Allowed", "Turned Off", "Not Available", "Unknown"];
const CENTRAL_MGMT_OPTIONS = ["Yes", "Partial", "No"];
const AUTH_TYPE_OPTIONS = ["Active Directory", "Individual"];

const empty = {
  name: "", description: "", category: "", systemType: "", server: "",
  accessOwners: [], billingOwners: [],
  auditIntervalMonths: 12,
  twoFactor: "Unknown", passwordRequirements: "", passwordExpiry: "",
  centralManagement: "No", securityNotes: "",
  authType: "", securityGroup: "", authNotes: "",
  accessLevels: [],
  imageVersion: 0,
};

function calcNextAuditDue(system, months) {
  const base = system?.lastAuditDate ? new Date(system.lastAuditDate)
    : system?.createdAt ? new Date(system.createdAt) : new Date();
  const next = new Date(base);
  next.setMonth(next.getMonth() + months);
  return next.toISOString();
}

function AccessLevelEditor({ levels, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const val = input.trim();
    if (!val || levels.includes(val)) return;
    onChange([...levels, val]);
    setInput("");
  };
  const remove = (l) => onChange(levels.filter(x => x !== l));
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10, minHeight: 26 }}>
        {levels.map(l => (
          <div key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "var(--purple-light)", borderRadius: 99, padding: "3px 10px" }}>
            <span style={{ fontSize: 12, color: "var(--purple-dark)", fontWeight: 500 }}>{l}</span>
            <button type="button" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--purple-dark)", fontSize: 12, padding: 0, lineHeight: 1 }} onClick={() => remove(l)}>✕</button>
          </div>
        ))}
        {levels.length === 0 && <span style={{ fontSize: 12, color: "var(--gray-400)" }}>Using global default access levels</span>}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="e.g. Admin, Viewer…" style={{ flex: 1 }} />
        <button type="button" className="btn-secondary" onClick={add}>Add</button>
      </div>
    </div>
  );
}

// Multi-person picker — search adds to a list of chips, each removable.
// Reuses PeoplePicker for the search box; remounts it after each pick
// (via `key`) so its internal query/value resets ready for the next search.
function MultiPeoplePicker({ people, onChange, placeholder }) {
  const [pickerKey, setPickerKey] = useState(0);

  const addPerson = (person) => {
    if (!person) return;
    if (people.some(p => p.upn === person.upn)) { setPickerKey(k => k + 1); return; }
    onChange([...people, { upn: person.upn, displayName: person.displayName, email: person.email }]);
    setPickerKey(k => k + 1);
  };
  const removePerson = (upn) => onChange(people.filter(p => p.upn !== upn));

  return (
    <div>
      <PeoplePicker key={pickerKey} value={null} onChange={addPerson} placeholder={placeholder} ownerOnly={true} />
      {people.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {people.map(p => (
            <div key={p.upn} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--blue-light)", borderRadius: 99, padding: "4px 10px" }}>
              <span style={{ fontSize: 12, fontWeight: 500 }}>{p.displayName}</span>
              <button type="button" onClick={() => removePerson(p.upn)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// A field, one per row — label above, help text below if given.
function Field({ label, required, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: "var(--gray-700)", display: "block", marginBottom: 5 }}>
        {label}{required && <span style={{ color: "var(--red)", marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// A titled card grouping related fields — the main visual unit of the page.
function FormCard({ title, description, adminOnly, children }) {
  return (
    <div className="card" style={{ marginBottom: 16, padding: "20px 24px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: "var(--gray-800)" }}>{title}</h2>
        {adminOnly && <span className="badge badge-neutral" style={{ fontSize: 10 }}>Admins only</span>}
      </div>
      {description && <p style={{ fontSize: 13, color: "var(--gray-500)", marginBottom: 16 }}>{description}</p>}
      {children}
    </div>
  );
}

export default function LocalApplicationsFormPage({ isAdmin = true }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const backTo = isAdmin ? (isEdit ? `/local-applications/${id}` : "/local-applications") : `/owner-local-application/${id}`;

  const [original, setOriginal] = useState(null); // the loaded item, for edit mode
  const [form,   setForm]   = useState(empty);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => { loadConfig().then(setConfig); }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const sys = await api.get(id);
        setOriginal(sys);
        setForm({ ...empty, ...sys });
      } catch {
        navigate(backTo);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]); // run once per :id param change

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleIntervalChange = (val) => {
    const nextDue = calcNextAuditDue(isEdit ? original : null, Number(val));
    setForm(f => ({ ...f, auditIntervalMonths: Number(val), nextAuditDue: nextDue }));
  };

  const handleImageUploaded = (newVersion) => {
    setForm(f => ({ ...f, imageVersion: newVersion }));
    if (original) setOriginal(o => ({ ...o, imageVersion: newVersion }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())     { setError("Local Application name is required"); return; }
    if (!form.category.trim()) { setError("Category is required"); return; }
    if ((form.accessOwners || []).length === 0) { setError("At least one Access Owner is required"); return; }
    setSaving(true); setError("");
    try {
      const payload = {
        ...form,
        nextAuditDue: form.nextAuditDue || calcNextAuditDue(isEdit ? original : null, form.auditIntervalMonths),
      };
      const saved = isEdit ? await api.update(id, payload) : await api.create(payload);
      navigate(isAdmin ? `/local-applications/${saved.id}` : `/owner-local-application/${saved.id}`);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${form.name}"? This will also remove all its users. This cannot be undone.`)) return;
    setDeleting(true); setError("");
    try {
      await api.remove(id);
      navigate("/local-applications");
    } catch (err) { setError(err.message); setDeleting(false); }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 80 }}><span className="spinner" /></div>;

  const previewDate = new Date(calcNextAuditDue(isEdit ? original : null, form.auditIntervalMonths))
    .toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  const categories     = config?.categories     || [];
  const auditIntervals = config?.auditIntervals || [1, 2, 3, 6, 12];

  const col2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      {/* Header: back link, title, and the primary actions — always visible, no scrolling needed to save */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <button type="button" className="btn-ghost" style={{ paddingLeft: 0, color: "var(--purple)", marginBottom: 6 }} onClick={() => navigate(backTo)}>
            ← {isEdit ? "Back to Local Application" : "Back to Local Applications"}
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>
            {isEdit ? `Edit Local Application${form.name ? `: ${form.name}` : ""}` : "Add New Local Application"}
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {isAdmin && isEdit && (
            <button type="button" className="btn-ghost" style={{ color: "var(--red)" }} onClick={handleDelete} disabled={deleting || saving}>
              {deleting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Deleting…</> : "Delete"}
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={() => navigate(backTo)}>Cancel</button>
          <button type="submit" form="local-application-form" className="btn-primary" disabled={saving}>
            {saving
              ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
              : (isEdit ? "Save Changes" : "Add Local Application")}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: "var(--red-light)", color: "var(--red)", padding: "10px 14px", borderRadius: "var(--radius)", marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}

      {isEdit && isAdmin && (
        <div style={{ background: "var(--orange-light)", color: "#7A3C00", padding: "10px 14px", borderRadius: "var(--radius)", marginBottom: 16, fontSize: 13 }}>
          System owners can see everything on this page except Admin Notes. Use Admin Notes for anything you don't want them to see.
        </div>
      )}

      <form id="local-application-form" onSubmit={handleSubmit}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20, alignItems: "start" }}>

          {/* ── Main column ── */}
          <div>
            <FormCard title="Local Application Details" description="What this application is and how it's categorised.">
              <Field label="Name" required>
                <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. AutoCAD, internal helpdesk tool" />
              </Field>
              <Field label="Description" hint="A short note on what this application is used for.">
                <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={3}
                  placeholder="What is this application used for?" style={{ width: "100%", resize: "vertical" }} />
              </Field>
              <div style={col2}>
                <Field label="Category" required>
                  <select value={form.category} onChange={e => set("category", e.target.value)}>
                    <option value="">Select category…</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Type">
                  <select value={form.systemType} onChange={e => set("systemType", e.target.value)}>
                    <option value="">Select type…</option>
                    {SYSTEM_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Server">
                <input value={form.server} onChange={e => set("server", e.target.value)} placeholder="e.g. APP-SVR-01, 10.0.4.12" />
              </Field>
            </FormCard>

            <FormCard title="Security" description="How well protected this application is, and how it's managed.">
              <div style={col2}>
                <Field label="2FA">
                  <select value={form.twoFactor} onChange={e => set("twoFactor", e.target.value)}>
                    {TWO_FACTOR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Central Management">
                  <select value={form.centralManagement} onChange={e => set("centralManagement", e.target.value)}>
                    {CENTRAL_MGMT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <div style={col2}>
                <Field label="Password Requirements">
                  <input value={form.passwordRequirements} onChange={e => set("passwordRequirements", e.target.value)} placeholder="e.g. 12+ characters, complexity enforced" />
                </Field>
                <Field label="Password Expiry">
                  <input value={form.passwordExpiry} onChange={e => set("passwordExpiry", e.target.value)} placeholder="e.g. 90 days, Never" />
                </Field>
              </div>
              <Field label="Notes">
                <input value={form.securityNotes} onChange={e => set("securityNotes", e.target.value)} placeholder="Any other security-relevant notes…" />
              </Field>
            </FormCard>

            <FormCard title="Ownership & Audit Schedule" description="Who's responsible for this application, and how often access gets reviewed.">
              <Field label="Access Owner" required hint="Determines who sees this under 'Local Applications I Own'. Supports multiple people.">
                <MultiPeoplePicker people={form.accessOwners || []} onChange={val => set("accessOwners", val)} placeholder="Search Azure AD…" />
              </Field>
              <Field label="Billing Owner" hint="Informational only — doesn't affect 'Local Applications I Own'.">
                <MultiPeoplePicker people={form.billingOwners || []} onChange={val => set("billingOwners", val)} placeholder="Search Azure AD…" />
              </Field>
              <Field label="Audit Every" required hint={<>Next audit will fall due <strong>{previewDate}</strong></>}>
                <select value={form.auditIntervalMonths} onChange={e => handleIntervalChange(e.target.value)}>
                  {auditIntervals.map(i => <option key={i} value={i}>{i} {i === 1 ? "month" : "months"}</option>)}
                </select>
              </Field>
            </FormCard>

            <FormCard title="Authentication" description="How people sign in, and anything worth noting about it.">
              <div style={col2}>
                <Field label="Type">
                  <select value={form.authType} onChange={e => set("authType", e.target.value)}>
                    <option value="">Select type…</option>
                    {AUTH_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Security Group">
                  <input value={form.securityGroup} onChange={e => set("securityGroup", e.target.value)} placeholder="e.g. SG-AutoCAD-Users" />
                </Field>
              </div>
              <Field label="Notes">
                <textarea value={form.authNotes} onChange={e => set("authNotes", e.target.value)} rows={3}
                  placeholder="e.g. Local accounts managed via AD group policy…" style={{ width: "100%", resize: "vertical" }} />
              </Field>
            </FormCard>

            {isAdmin && (
              <FormCard title="Access Levels" adminOnly description="The roles/permissions available when assigning users to this application.">
                <AccessLevelEditor levels={form.accessLevels || []} onChange={val => set("accessLevels", val)} />
              </FormCard>
            )}
          </div>

          {/* ── Sidebar: image + live preview, sticky so it stays visible while scrolling the form ── */}
          <div style={{ position: "sticky", top: 20 }}>
            {isAdmin && isEdit && (
              <div className="card" style={{ marginBottom: 16, padding: "18px 20px" }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--gray-700)" }}>Local Application Icon</h3>
                <SystemImageUpload
              resource="local-applications"
                  systemId={id}
                  systemName={form.name}
                  imageVersion={form.imageVersion}
                  onUploaded={handleImageUploaded}
                />
              </div>
            )}
            {isAdmin && !isEdit && (
              <div className="card" style={{ marginBottom: 16, padding: "18px 20px" }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: "var(--gray-700)" }}>Local Application Icon</h3>
                <p style={{ fontSize: 12, color: "var(--gray-500)" }}>Save the application first, then come back here to add an icon.</p>
              </div>
            )}

            <div className="card" style={{ padding: "18px 20px" }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "var(--gray-700)" }}>Preview</h3>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, wordBreak: "break-word" }}>{form.name || <span style={{ color: "var(--gray-400)" }}>Untitled application</span>}</div>
              {form.category && <span className="badge badge-info" style={{ fontSize: 11, marginBottom: 8, display: "inline-block" }}>{form.category}</span>}
              {form.systemType && <span className="badge badge-neutral" style={{ fontSize: 11, marginBottom: 8, marginLeft: 6, display: "inline-block" }}>{form.systemType}</span>}
              <div style={{ fontSize: 12, color: "var(--gray-500)", marginTop: 10, lineHeight: 1.7 }}>
                <div>👤 {(form.accessOwners||[]).length ? form.accessOwners.map(o=>o.displayName).join(", ") : <em>No access owner set</em>}</div>
                {(form.billingOwners||[]).length > 0 && <div>💳 {form.billingOwners.map(o=>o.displayName).join(", ")}</div>}
                <div>🔁 Reviewed every {form.auditIntervalMonths} month{form.auditIntervalMonths !== 1 ? "s" : ""}</div>
                <div>📅 Next review: {previewDate}</div>
                <div>🔐 2FA: {form.twoFactor}</div>
              </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
}
